// What a partner did on the site: one document per page they opened.
//
// Written by the browser as partners move around the catalogue (see
// `src/hooks/useTrackActivity.ts`) and read back by the CMS on the partner edit
// page. It exists because the question we actually want answered — "has this
// partner looked at anything since we invited them, and at what" — is about a
// named account, which is the one thing web analytics is worst at: standard
// reports lag a day, rows covering a handful of partners are withheld as too
// small to be anonymous, and a corporate network or ad blocker drops the tag
// without telling anyone. A write to our own Firestore lands.
//
// This is an engagement signal, not an audit trail. Events are written by the
// client, so a partner could in principle inflate their own numbers; what the
// security rules do guarantee is that nobody can write events attributed to
// somebody else. Our own browsing is not recorded at all — the tracking hook
// skips admins — so a count here is the partner's use of the site, not ours.

// Firestore collection name.
export const coll_activity = 'activity';

export type ActivityActionType =
  | 'pageView'
  | 'experienceView'
  | 'collectionView'
  | 'bookingRequestSubmitted';

export const activityActionLabels: Record<ActivityActionType, string> = {
  pageView: 'Page view',
  experienceView: 'Experience view',
  collectionView: 'Collection view',
  bookingRequestSubmitted: 'Booking request',
};

/**
 * How long a raw event is kept, in days.
 *
 * Every event carries an `expireAt` timestamp this far out, so a Firestore TTL
 * policy on that field prunes the collection without a scheduled job. The
 * policy has to be switched on in the console once (Firestore → TTL, collection
 * `activity`, field `expireAt`); until it is, the field is simply unused.
 *
 * Just over a year, so a partner's activity can always be read against the same
 * month a year earlier.
 */
export const activityRetentionDays = 400;

/** What an event says about itself. The rest is filled in from who is signed in. */
export interface ActivityDraftType {
  action: ActivityActionType;
  /** The route, as the browser saw it. */
  path: string;
  /** The experience or collection this is about, when it is about one. */
  targetId?: string;
}

export interface ActivityType extends ActivityDraftType {
  id: string;

  // Who. `partnerId` is what the CMS queries by; `uid` and `userEmail` are the
  // individual concierge, denormalised so the admin panel can break a partner's
  // activity down by account without a lookup per row.
  partnerId: string;
  uid: string;
  userEmail: string;

  /** ISO timestamp, from the client's clock. */
  createdAt: string;
}

const experiencePathPattern = /^\/experiences\/([^/]+)/;
const collectionPathPattern = /^\/collections\/([^/]+)/;

/**
 * What a route means as an activity event.
 *
 * The route → event mapping lives here rather than in the tracking hook so that
 * the admin panel reading `targetId` back and the browser writing it agree on
 * what it holds — an experience id on an experience page, a collection id on a
 * collection page, and nothing anywhere else.
 */
export function describeActivityPath(pathname: string): ActivityDraftType {
  const experience = experiencePathPattern.exec(pathname);
  if (experience) return { action: 'experienceView', path: pathname, targetId: experience[1] };

  const collection = collectionPathPattern.exec(pathname);
  if (collection) return { action: 'collectionView', path: pathname, targetId: collection[1] };

  return { action: 'pageView', path: pathname };
}
